import { Component, Injector, OnDestroy, ChangeDetectorRef, HostListener } from '@angular/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { AppComponentBase } from 'src/app/app-component-base';
import { AppConsts } from '../shared/AppConsts';
import { VersionCheckService } from '../shared/services/version.check.service';
import { AppComponent } from './app.component';
import { UserServiceProxy, UserNotificationsServiceProxy, UserNotificationDto } from '../shared/service-proxies/service-proxies';
import { DomSanitizer } from '@angular/platform-browser';
import * as signalR from '@microsoft/signalr';
import { AuthenticationService } from './_services/authentication.service';

@Component({
    selector: 'app-topbar',
    templateUrl: './app.topbar.component.html',
    providers: [MessageService]
})
export class AppTopBarComponent extends AppComponentBase implements OnDestroy {

    multiTenancy: boolean = AppConsts.multiTenancy;
    versionChanged: boolean;
    unreadNotificationsCount: number = 0;
    notifications: UserNotificationDto[] = [];
    showNotificationsDropdown: boolean = false;
    tenantId: number | null = null;

    // Modal de notificaciones
    showNotificationsModal: boolean = false;
    allNotifications: UserNotificationDto[] = [];
    pagedNotifications: UserNotificationDto[] = [];
    filteredNotifications: UserNotificationDto[] = [];
    currentNotificationPage: number = 0;
    notificationsPerPage: number = 10;
    totalNotifications: number = 0;
    
    // Filtros de notificaciones
    selectedNotificationFilter: string = 'all';
    notificationFilterOptions = [
        { label: 'Todas', value: 'all' },
        { label: 'No leídas', value: 'unread' },
        { label: 'Leídas', value: 'read' }
    ];
    
    private signalRConnection: signalR.HubConnection;
    private cachedAuthToken: string | null = null;

    constructor(public app: AppComponent,
        injector: Injector,
        private versionCheckService: VersionCheckService,
        private confirmationService: ConfirmationService,
        private userService: UserServiceProxy,
        private userNotificationsService: UserNotificationsServiceProxy,
        private domSanitizer: DomSanitizer,
        private authenticationService: AuthenticationService,
        private cdr: ChangeDetectorRef) {
        super(injector);

        this.versionCheckService.versionChanged.subscribe(x => {
            this.versionChanged = x;

            if (x) {
                this.confirmationService.confirm({
                    header: this.l('NewVersion'),
                    icon: 'pi pi-info',
                    message: this.l('NewAppVersionMessage'),
                    acceptLabel: this.l('UpdateNow'),
                    rejectLabel: this.l('Cancel'),
                    accept: () => {
                        this.updateAppVersion();
                    }
                });
            }
        });

        this.updateImageProfile();

        const currentUser = this.authenticationService?.currentUserValue;
        this.tenantId = currentUser?.tenantId || null;
        if (this.tenantId === 6) {
            this.loadNotificationsCount();
            this.loadNotifications();
            this.initializeSignalRNotifications();
        }       
    }

    updateImageProfile(): void {
        const self = this;

        /*this.urlPictureProfile = this.getBaseServiceUrl() + '/api/User/GetPictureProfile?id=' +
            this.app.currentUser.userId + '&v' + (new Date().getTime());*/

        self.userService.getPictureProfile()
            .subscribe(data => {
                if (data.data) {

                    const reader = new FileReader();

                    reader.onloadend = () => {
                        const base64data = reader.result as string;
                        // self.urlPictureProfile = self.domSanitizer.bypassSecurityTrustUrl(base64data);
                        self.urlPictureProfile = base64data;
                    };

                    // reader.onerror = reject;
                    reader.readAsDataURL(data.data);
                }

            });
    }

    updateAppVersion(): void {
        this.versionCheckService.updateVersion();
    }

    loadNotificationsCount(): void {
        this.userNotificationsService.getUnreadCount()
            .subscribe(
                count => {
                    this.unreadNotificationsCount = count;
                },
                error => {
                    // Silenciar errores durante redesplegajes para no interferir con sistema de versiones
                    this.unreadNotificationsCount = 0;
                }
            );
    }

    loadNotifications(): void {
        this.userNotificationsService.getNotifications(false, 10)
            .subscribe(
                notifications => {
                    this.notifications = notifications;
                },
                error => {
                    // Silenciar errores durante redesplegajes para no interferir con sistema de versiones
                    this.notifications = [];
                }
            );
    }

    toggleNotificationsDropdown(event: Event): void {
        event.preventDefault();
        event.stopPropagation();
        this.showNotificationsDropdown = !this.showNotificationsDropdown;
        
        // Recargar notificaciones cuando se abre el dropdown
        if (this.showNotificationsDropdown) {
            this.loadNotificationsCount();
            this.loadNotifications();
        }
    }

    markNotificationAsRead(notification: UserNotificationDto, event: Event): void {
        event.preventDefault();
        event.stopPropagation();
        
        if (!notification.isRead) {
            this.userNotificationsService.markAsRead(notification.id)
                .subscribe(
                    () => {
                        notification.isRead = true;
                        notification.readedAt = new Date();
                        
                        // Actualizar contador
                        this.loadNotificationsCount();
                        
                        // Si el modal está abierto, refrescar los datos del modal
                        if (this.showNotificationsModal) {
                            this.applyNotificationFilter();
                        }
                    },
                    error => {
                        // Silenciar errores durante redesplegajes
                        // Marcar como leída localmente al menos
                        notification.isRead = true;
                        notification.readedAt = new Date();
                    }
                );
        }
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: Event): void {
        const target = event.target as HTMLElement;
        
        // Verificar si el clic fue dentro del área de notificaciones
        const notificationArea = target.closest('.layout-topbar-notifications');
        const notificationModal = target.closest('.p-dialog');
        
        // Solo cerrar si el clic fue fuera del área de notificaciones y no en el modal
        if (!notificationArea && !notificationModal && this.showNotificationsDropdown) {
            this.showNotificationsDropdown = false;
        }
    }

    // Función para obtener solo las notificaciones no leídas para el dropdown
    getUnreadNotifications(): UserNotificationDto[] {
        return this.notifications.filter(n => !n.isRead);
    }

    // Función para abrir el modal de todas las notificaciones
    openNotificationsModal(event: Event): void {
        event.preventDefault();
        event.stopPropagation();
        this.showNotificationsDropdown = false;
        this.showNotificationsModal = true;
        this.loadAllNotifications();
    }

    // Función para limpiar HTML de los mensajes de notificación para toast
    private cleanHtmlFromMessage(htmlMessage: string): string {
        if (!htmlMessage) return '';
        
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = htmlMessage;
        
        let cleanText = tempDiv.textContent || tempDiv.innerText || '';
        cleanText = cleanText.replace(/\s+/g, ' ').trim();
        
        if (cleanText.length > 100) {
            cleanText = cleanText.substring(0, 97) + '...';
        }
        
        return cleanText;
    }

    // Cargar todas las notificaciones para el modal (con más cantidad)
    loadAllNotifications(): void {
        // Cargar más notificaciones para el modal (200 para asegurar que tengamos todas)
        this.userNotificationsService.getNotifications(false, 200)
            .subscribe(
                notifications => {
                    this.allNotifications = notifications;
                    this.selectedNotificationFilter = 'all'; // Reset filter
                    this.applyNotificationFilter();
                },
                error => {
                    // Silenciar errores durante redesplegajes
                    this.allNotifications = [];
                    this.applyNotificationFilter();
                }
            );
    }

    // Aplicar filtro de notificaciones
    applyNotificationFilter(): void {
        switch (this.selectedNotificationFilter) {
            case 'unread':
                this.filteredNotifications = this.allNotifications.filter(n => !n.isRead);
                break;
            case 'read':
                this.filteredNotifications = this.allNotifications.filter(n => n.isRead);
                break;
            default:
                this.filteredNotifications = [...this.allNotifications];
                break;
        }
        this.totalNotifications = this.filteredNotifications.length;
        this.currentNotificationPage = 0; // Reset a la primera página
        this.updatePagedNotifications();
    }

    // Actualizar notificaciones paginadas
    updatePagedNotifications(): void {
        const start = this.currentNotificationPage * this.notificationsPerPage;
        const end = start + this.notificationsPerPage;
        this.pagedNotifications = this.filteredNotifications.slice(start, end);
        // console.log(`📄 Página ${this.currentNotificationPage + 1}: Mostrando ${this.pagedNotifications.length} de ${this.totalNotifications} total (${start + 1}-${Math.min(end, this.totalNotifications)})`);
    }

    // Manejar cambio de filtro
    onNotificationFilterChange(): void {
        this.applyNotificationFilter();
    }

    // Manejar cambio de página
    onNotificationPageChange(event: any): void {
        this.currentNotificationPage = event.page;
        this.updatePagedNotifications();
    }

    private async initializeSignalRNotifications(): Promise<void> {
        try {
            const authToken = this.getAuthTokenFromStorage();
            if (!authToken) {
                // console.warn('⚠️ No hay token de autenticación, usando fallback polling');
                this.startFallbackPolling();
                return;
            }
            
            this.signalRConnection = new signalR.HubConnectionBuilder()
                .withUrl(this.getBaseServiceUrl() + '/notificationHub', {
                    accessTokenFactory: () => {
                        const token = this.getAuthTokenFromStorage();
                        return token || '';
                    },
                    skipNegotiation: false,
                    transport: signalR.HttpTransportType.ServerSentEvents | signalR.HttpTransportType.LongPolling
                })
                .withAutomaticReconnect()
                .configureLogging(signalR.LogLevel.Warning) // Reducir ruido en logs
                .build();

            // Configurar los listeners para las notificaciones EN TIEMPO REAL
            this.signalRConnection.on('NewNotification', (notification) => {
                try {
                    // Mostrar notificación visual al usuario (limpiar HTML para el toast)
                    this.notify.warning(this.cleanHtmlFromMessage(notification.message));
                    
                    // Recargar datos para actualizar contadores y listas
                    this.loadNotificationsCount();
                    this.loadNotifications();
                    
                    // Si el modal está abierto, también refrescar sus datos
                    if (this.showNotificationsModal) {
                        this.loadAllNotifications();
                    }
                } catch (error) {
                    // Silenciar errores de notificaciones durante redesplegajes
                }
            });

            this.signalRConnection.on('UpdateNotificationCount', (count) => {
                // Solo actualizar si el count es un número válido
                if (typeof count === 'number' && count >= 0) {
                    this.unreadNotificationsCount = count;
                    this.cdr.detectChanges();
                }
            });

            // Manejar reconexión automática
            this.signalRConnection.onreconnected(() => {
                this.loadNotificationsCount();
                this.loadNotifications();
            });

            // Iniciar la conexión
            await this.signalRConnection.start();

            // Unirse al grupo de notificaciones del usuario
            await this.signalRConnection.invoke('JoinNotificationGroup');

        } catch (error) {
            // Silenciar errores de SignalR durante redesplegajes para no interferir con sistema de versiones
            // Solo usar fallback polling sin mostrar errores
            this.startFallbackPolling();
        }
    }

    private getAuthTokenFromStorage(): string {
        // Usar token cacheado si está disponible
        if (this.cachedAuthToken) {
            return this.cachedAuthToken;
        }

        try {
            // Usar exactamente la misma lógica que los service-proxies
            const prefixedKey = AppConsts.appPrefix + '-currentUser';
            const currentUserData = localStorage.getItem(prefixedKey);
            
            if (currentUserData) {
                // Verificar si ya es un objeto o necesita parsing
                let currentUser: any;
                
                if (typeof currentUserData === 'string') {
                    try {
                        currentUser = JSON.parse(currentUserData);
                        
                        // Si después del primer parse aún es string, hacer segundo parse (doble encoding)
                        if (typeof currentUser === 'string') {
                            currentUser = JSON.parse(currentUser);
                        }
                        
                    } catch (parseError) {
                        console.error('❌ Error parsing JSON:', parseError);
                        return '';
                    }
                } else {
                    currentUser = currentUserData;
                }
                
                // Verificar que es un objeto válido y no un string
                if (currentUser && typeof currentUser === 'object' && !Array.isArray(currentUser)) {
                    if (currentUser.token) {
                        // Cachear el token para evitar parsing repetitivo
                        this.cachedAuthToken = currentUser.token;
                        return currentUser.token;
                    }
                }
            }
            
            return '';
        } catch (error) {
            console.error('❌ Error obteniendo token del storage:', error);
            return '';
        }
    }

    private startFallbackPolling(): void {
        // Polling inicial
        this.loadNotificationsCount();
        this.loadNotifications();
        
        // Configurar interval para polling
        setInterval(() => {
            this.loadNotificationsCount();
            if (!this.showNotificationsDropdown && !this.showNotificationsModal) {
                this.loadNotifications();
            }
        }, 60000); // 60 segundos
    }

    ngOnDestroy(): void {
        // Limpiar cache y conexión SignalR cuando se destruya el componente
        this.cachedAuthToken = null;
        
        if (this.signalRConnection) {
            this.signalRConnection.stop().then(() => {
                // console.log('SignalR desconectado');
            }).catch(err => {
                console.error('Error desconectando SignalR:', err);
            });
        }
    }
}
